import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Inbox, Mail, Paperclip, RefreshCw, Send, Trash2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { mailboxReadStateHint, mailboxReadStateMode } from "@/lib/platform";
import { formatAuditTimestamp } from "@/lib/auditFormat";
import { Button } from "@/components/ui/Button";
import type { Guest } from "@/types/camping";
import { MailMessageBody } from "@/components/mail/MailMessageBody";

type MailFolder = "inbox" | "sent" | "trash";

interface GuestMailFolderCounts {
  total: number;
  unread: number;
}

interface GuestMailMessage {
  id: string;
  folder: MailFolder;
  subject: string;
  direction: "incoming" | "outgoing";
  contact_name: string | null;
  contact_address: string;
  timestamp: string;
  is_read: boolean;
  body_preview: string;
  has_attachments: boolean;
}

interface GuestMailListResponse {
  mailbox: string;
  guest_email: string;
  items: GuestMailMessage[];
  unread_count: number;
  inbox: GuestMailFolderCounts;
  sent: GuestMailFolderCounts;
  trash: GuestMailFolderCounts;
}

interface MailDetail {
  id: string;
  subject: string;
  body_text: string;
  body_html: string;
  is_read: boolean;
  from_name?: string | null;
  from_address?: string;
  to_address?: string;
  to_addresses?: string[];
  received_at?: string;
  sent_at?: string;
}

function FolderUnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="rounded-full bg-status-orange/20 px-1.5 text-[0.65rem] font-semibold text-status-orange">
      {count}
    </span>
  );
}

function ReadStatusBadge({ isRead }: { isRead: boolean }) {
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-medium",
        isRead
          ? "bg-fill-tertiary text-label-secondary"
          : "bg-status-orange/15 text-status-orange",
      )}
    >
      {isRead ? "Gelesen" : "Ungelesen"}
    </span>
  );
}

const FOLDER_CONFIG: {
  key: MailFolder;
  label: string;
  icon: typeof Inbox;
  countsKey: keyof Pick<GuestMailListResponse, "inbox" | "sent" | "trash">;
}[] = [
  { key: "inbox", label: "Posteingang", icon: Inbox, countsKey: "inbox" },
  { key: "sent", label: "Postausgang", icon: Send, countsKey: "sent" },
  { key: "trash", label: "Papierkorb", icon: Trash2, countsKey: "trash" },
];

export function GuestMailPanel({ guest, active }: { guest: Guest; active: boolean }) {
  const [data, setData] = useState<GuestMailListResponse | null>(null);
  const [folder, setFolder] = useState<MailFolder>("inbox");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MailDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(() => {
    if (!guest.id || !guest.email?.trim()) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get<GuestMailListResponse>(`/api/v1/guests/${guest.id}/emails`)
      .then((result) => {
        setData(result);
        setSelectedId(null);
        setDetail(null);
      })
      .catch((err: unknown) => {
        setData(null);
        setError(err instanceof Error ? err.message : "E-Mails konnten nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, [guest.email, guest.id]);

  useEffect(() => {
    if (!active) return;
    load();
  }, [active, load]);

  const folderMessages = useMemo(() => {
    if (!data) return [];
    return data.items.filter((message) => message.folder === folder);
  }, [data, folder]);

  const readStateMode = mailboxReadStateMode();
  const readStateHint = mailboxReadStateHint(readStateMode);

  const markMessageRead = useCallback((messageId: string) => {
    if (mailboxReadStateMode() !== "live") return;
    setData((current) =>
      current
        ? {
            ...current,
            items: current.items.map((message) =>
              message.id === messageId ? { ...message, is_read: true } : message,
            ),
            unread_count: Math.max(
              0,
              current.unread_count -
                (current.items.find((message) => message.id === messageId && !message.is_read)
                  ? 1
                  : 0),
            ),
            inbox: {
              ...current.inbox,
              unread: Math.max(
                0,
                current.inbox.unread -
                  (current.items.find(
                    (message) =>
                      message.id === messageId && message.folder === "inbox" && !message.is_read,
                  )
                    ? 1
                    : 0),
              ),
            },
            sent: {
              ...current.sent,
              unread: Math.max(
                0,
                current.sent.unread -
                  (current.items.find(
                    (message) =>
                      message.id === messageId && message.folder === "sent" && !message.is_read,
                  )
                    ? 1
                    : 0),
              ),
            },
            trash: {
              ...current.trash,
              unread: Math.max(
                0,
                current.trash.unread -
                  (current.items.find(
                    (message) =>
                      message.id === messageId && message.folder === "trash" && !message.is_read,
                  )
                    ? 1
                    : 0),
              ),
            },
          }
        : current,
    );
    void api
      .patch(`/api/v1/mail/inbox/${encodeURIComponent(messageId)}/read`)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!selectedId || !data) {
      setDetail(null);
      return;
    }
    const message = data.items.find((item) => item.id === selectedId);
    if (!message) {
      setDetail(null);
      return;
    }

    setDetailLoading(true);
    const endpoint =
      message.folder === "sent"
        ? `/api/v1/mail/sent/${encodeURIComponent(selectedId)}`
        : `/api/v1/mail/inbox/${encodeURIComponent(selectedId)}`;

    void api
      .get<MailDetail>(endpoint)
      .then((result) => {
        setDetail(result);
        if (!result.is_read && message.folder !== "sent") {
          markMessageRead(selectedId);
        }
      })
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [data, markMessageRead, selectedId]);

  if (!active) return null;

  if (!guest.email?.trim()) {
    return (
      <section className="rounded-xl border border-separator bg-surface-secondary/40 p-4">
        <h3 className="font-medium text-label">E-Mail-Korrespondenz</h3>
        <p className="mt-2 text-sm text-label-secondary">
          Für diesen Gast ist keine E-Mail-Adresse hinterlegt.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-separator bg-surface-secondary/40 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-medium text-label">E-Mail-Korrespondenz</h3>
          {data ? (
            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-label-secondary">
              <Mail size={14} aria-hidden />
              {data.guest_email}
              {data.unread_count > 0 ? (
                <span className="rounded-full bg-status-orange/20 px-2 py-0.5 text-xs font-semibold text-status-orange">
                  {data.unread_count} ungelesen
                </span>
              ) : null}
              {readStateHint ? <span className="text-xs text-label-tertiary">{readStateHint}</span> : null}
            </p>
          ) : null}
        </div>
        <Button type="button" variant="secondary" onClick={() => load()} disabled={loading}>
          <RefreshCw size={16} className="mr-1.5 inline" aria-hidden />
          Aktualisieren
        </Button>
      </div>

      <div
        className="eh-segmented eh-segmented--md mt-4 min-w-max"
        role="tablist"
        aria-label="E-Mail-Ordner"
      >
        {FOLDER_CONFIG.map(({ key, label, icon: Icon, countsKey }) => {
          const counts = data?.[countsKey];
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={folder === key}
              className={clsx(
                "eh-segmented-item flex items-center gap-1.5",
                folder === key && "is-active",
              )}
              onClick={() => {
                setFolder(key);
                setSelectedId(null);
                setDetail(null);
              }}
            >
              <Icon size={14} aria-hidden />
              {label}
              {counts ? <FolderUnreadBadge count={counts.unread} /> : null}
            </button>
          );
        })}
      </div>

      {error ? <p className="mt-3 text-sm text-status-red">{error}</p> : null}
      {loading ? (
        <p className="mt-4 text-sm text-label-secondary">E-Mails werden geladen…</p>
      ) : null}

      {!loading && !error && data && folderMessages.length === 0 ? (
        <p className="mt-4 text-sm text-label-secondary">
          Keine E-Mails in {FOLDER_CONFIG.find((item) => item.key === folder)?.label ?? "diesem Ordner"}.
        </p>
      ) : null}

      {!loading && folderMessages.length > 0 ? (
        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(14rem,20rem)_1fr]">
          <ul className="max-h-[28rem] space-y-1 overflow-y-auto rounded-lg border border-separator bg-card">
            {folderMessages.map((message) => {
              const activeMessage = message.id === selectedId;
              return (
                <li key={message.id}>
                  <button
                    type="button"
                    className={clsx(
                      "w-full border-b border-separator/70 px-3 py-3 text-left transition last:border-b-0",
                      activeMessage ? "bg-accent-soft" : "hover:bg-fill-tertiary/60",
                      !message.is_read && !activeMessage && "bg-fill-tertiary/30",
                    )}
                    onClick={() => setSelectedId(message.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span
                        className={clsx(
                          "truncate text-sm",
                          message.is_read ? "font-medium text-label" : "font-bold text-label",
                        )}
                      >
                        {message.contact_name || message.contact_address}
                      </span>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span className="text-[0.65rem] text-label-tertiary">
                          {formatAuditTimestamp(message.timestamp)}
                        </span>
                        <ReadStatusBadge isRead={message.is_read} />
                      </div>
                    </div>
                    <p
                      className={clsx(
                        "mt-0.5 truncate text-sm",
                        message.is_read ? "text-label-secondary" : "font-medium text-label",
                      )}
                    >
                      {message.subject}
                    </p>
                    <p className="mt-1 line-clamp-2 text-xs text-label-tertiary">
                      {message.body_preview}
                    </p>
                    {message.has_attachments ? (
                      <span className="mt-1 inline-flex items-center gap-1 text-[0.65rem] text-label-tertiary">
                        <Paperclip size={12} aria-hidden />
                        Anhang
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="min-h-[12rem] rounded-lg border border-separator bg-card">
            {!selectedId ? (
              <p className="flex h-full items-center justify-center px-4 py-8 text-center text-sm text-label-secondary">
                Wählen Sie eine Nachricht aus, um den Inhalt anzuzeigen.
              </p>
            ) : detailLoading ? (
              <p className="px-4 py-8 text-sm text-label-secondary">Nachricht wird geladen…</p>
            ) : detail ? (
              <div className="flex h-full flex-col">
                <header className="border-b border-separator px-4 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <h4 className="text-base font-semibold text-label">{detail.subject}</h4>
                    <ReadStatusBadge isRead={detail.is_read} />
                  </div>
                  {detail.from_address ? (
                    <p className="mt-1 text-sm text-label-secondary">
                      Von{" "}
                      <span className="font-medium text-label">
                        {detail.from_name
                          ? `${detail.from_name} <${detail.from_address}>`
                          : detail.from_address}
                      </span>
                    </p>
                  ) : null}
                  <p className="text-xs text-label-tertiary">
                    {formatAuditTimestamp(detail.received_at || detail.sent_at || "")}
                    {detail.to_addresses && detail.to_addresses.length > 0
                      ? ` · An ${detail.to_addresses.join(", ")}`
                      : detail.to_address
                        ? ` · An ${detail.to_address}`
                        : null}
                  </p>
                </header>
                <div className="flex-1 overflow-y-auto px-4 py-3">
                  <MailMessageBody html={detail.body_html} text={detail.body_text} />
                </div>
              </div>
            ) : (
              <p className="px-4 py-8 text-sm text-status-red">
                Nachricht konnte nicht geladen werden.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
